import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { initMercadoPago, Payment, StatusScreen } from '@mercadopago/sdk-react';
import { Alert } from 'antd';

import CustomerDto from '@/application/dto/CustomerDto';
import PaymentRepository from '@/application/repositories/PaymentRepository';
import PaymentDto from '@/application/dto/PaymentDto';
import PixPaymentRepository from '@/application/repositories/PixPaymentRepository';
import PixPaymentDto from '@/application/dto/PixPaymentDto';
import CreditCardsBins from '@/application/repositories/CreditCardsBins';
import ProductDto from '@/application/dto/ProductDto';

import Loading from '@/components/Loading';
import Overlay from '@/components/Loading/Overlay';

import { NotePayment } from './style';
import {
  addErrorInButton,
  creditCardFieldError,
} from '../support-error-mercadopago';
import { cpf } from 'cpf-cnpj-validator';

initMercadoPago(process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY || '', {
  locale: 'pt-BR',
});

declare global {
  interface Window {
    paymentBrickController: {
      unmount: () => void;
    };
  }
}

export const MercadoPagoForm = () => {
  const { state } = useLocation();

  const [paymentId, setPaymentId] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [paymentError, setPaymentError] = useState();
  const [creditCardBinsAvailable, setCreditCardBinsAvailable] = useState<
    number[]
  >([]);
  const [processigPayment, setProcessingPayment] = useState(false);
  const creditCardBin = useRef<string>('');

  useEffect(() => {
    if (paymentId) {
      setLoading(true);
    }
  }, [paymentId]);

  useEffect(() => {
    if (window.paymentBrickController) {
      window.paymentBrickController.unmount();
    }

    (async () => {
      if (product.pageId) {
        const creditCardBins = new CreditCardsBins();
        setCreditCardBinsAvailable(
          await creditCardBins.getBinsAvailable(product.pageId as string)
        );
      }
    })();

    return () => {
      if (window.paymentBrickController) {
        window.paymentBrickController.unmount();
      }
    };
  }, []);

  const handlePixPaymentCreate = async (pixData: PixPaymentDto) => {
    try {
      setPaymentError(undefined);
      setProcessingPayment(true);

      const pixPaymentRepository = new PixPaymentRepository();
      const { paymentId } = await pixPaymentRepository.create(pixData);
      return paymentId;
    } catch (err: any) {
      setPaymentError(err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCreditCardPaymentCreate = async (paymentData: PaymentDto) => {
    try {
      setPaymentError(undefined);
      setProcessingPayment(true);

      const paymentRepository = new PaymentRepository();
      const { paymentId } = await paymentRepository.create(paymentData);
      return paymentId;
    } catch (err: any) {
      setPaymentError(err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const binValidate = useCallback(
    (bin: string): boolean => {
      if (creditCardBinsAvailable.length === 0) {
        return true;
      }

      return (
        creditCardBinsAvailable.filter((binCode) =>
          bin.includes(String(binCode))
        ).length > 0
      );
    },
    [creditCardBin, creditCardBinsAvailable]
  );

  const product = state.product as ProductDto;

  return (
    <div>
      <Overlay open={processigPayment} text="Processando..." />

      <NotePayment>
        <span className="material-icons">info</span>
        <div>
          <h1>
            Após a confirmação do pagamento o voucher será enviado para o seu
            email!
          </h1>
          <div>
            <p>
              Em caso de dúvidas você pode entrar em contato com nossa equipe de
              suporte através do nosso formulário:
              <a
                href="https://primepass.zendesk.com/hc/pt-br/requests/new"
                target="_blank"
                rel="noopener noreferrer"
              >
                Clique aqui
              </a>
            </p>
          </div>
        </div>
      </NotePayment>

      <Loading show={loading} text="Aguarde..." />

      {paymentError && (
        <Alert
          message={paymentError}
          style={{ marginBottom: '20px' }}
          type="error"
          closable
          showIcon
        />
      )}

      {!!paymentId && (
        <StatusScreen
          initialization={{
            paymentId: paymentId,
          }}
          onReady={() => {
            setLoading(false);
          }}
        />
      )}

      {!paymentId && (
        <Payment
          initialization={{
            amount: product.priceCurrent as number,
            payer: {
              email: state.customer.email,
            },
          }}
          customization={{
            paymentMethods: {
              bankTransfer: 'all',
              creditCard: 'all',
              maxInstallments: 1,
            },
          }}
          onSubmit={async ({
            formData: {
              payer,
              transaction_amount,
              token,
              payment_method_id,
              installments,
            },
          }) => {
            const customerDto = new CustomerDto();
            const productDto = new ProductDto();

            customerDto.email = state.customer.email;
            customerDto.name = state.customer.name;
            customerDto.document = cpf.strip(state.customer.document);
            productDto.id = product.id;
            productDto.description = product.description;
            productDto.name = product.name;

            if (payment_method_id === 'pix') {
              const pixPaymentDto = new PixPaymentDto();

              pixPaymentDto.amount = transaction_amount;
              pixPaymentDto.customer = customerDto;
              pixPaymentDto.product = productDto;
              pixPaymentDto.companyId = product.companyId;
              pixPaymentDto.pageId = product.pageId;

              setPaymentId(await handlePixPaymentCreate(pixPaymentDto));
              return;
            }

            if (!binValidate(creditCardBin.current)) {
              creditCardFieldError(
                true,
                'Cartão de crédito inválido para esse benefício'
              );
              addErrorInButton();
              return;
            } else {
              creditCardFieldError(false);
            }

            const paymentDto = new PaymentDto();
            customerDto.document = payer.identification.number;
            paymentDto.customer = customerDto;
            paymentDto.product = productDto;
            paymentDto.amount = transaction_amount;
            paymentDto.cardToken = token;
            paymentDto.installments = installments;
            paymentDto.companyId = product.companyId;
            paymentDto.pageId = product.pageId;

            setPaymentId(await handleCreditCardPaymentCreate(paymentDto));
          }}
          onReady={() => setLoading(false)}
          onBinChange={(bin) => {
            creditCardBin.current = bin;

            if (!binValidate(bin)) {
              creditCardFieldError(
                true,
                'Cartão de crédito inválido para esse benefício'
              );
              addErrorInButton(true);
            } else {
              creditCardFieldError(false);
              addErrorInButton(false);
            }
          }}
        />
      )}
    </div>
  );
};
